/**
 * The content validity index, item and scale level, with the chance-adjusted kappa.
 *
 * Experts rate each item's relevance on the 4-point scale Davis (1992) labelled
 * 1 = not relevant, 2 = somewhat relevant, 3 = quite relevant, 4 = highly relevant.
 *
 * - **I-CVI** — the share of experts rating an item 3 or 4 (Lynn, 1986).
 * - **S-CVI/Ave** — the mean of the I-CVIs; **S-CVI/UA** — the share of items every expert
 *   rated 3 or 4 (Polit & Beck, 2006, who recommend S-CVI/Ave ≥ .90).
 * - **Modified kappa k\*** (Polit, Beck & Owen, 2007): the chance that exactly A of N
 *   experts call an item relevant if each tossed a fair coin is
 *   Pc = [N! / (A!(N − A)!)] × 0.5^N, and k* = (I-CVI − Pc) / (1 − Pc). They grade it
 *   fair .40–.59, good .60–.74 and excellent > .74, after Cicchetti & Sparrow (1981) and
 *   Fleiss (1981).
 * - **Lynn's (1986) minimum** — Table 2 of her paper, in counts of experts who must agree:
 *   all of 3–5, then 5 of 6, 6 of 7, 7 of 8, 7 of 9 and 8 of 10. Polit & Beck (2006)
 *   quote it as I-CVI = 1.00 for 3–5 experts and no lower than .78 for 6–10, the floor
 *   applied here beyond ten experts.
 */

import { parseNumberInput } from "@/lib/parse-number";
import { mean } from "@/lib/stats/descriptive";
import { binomialPmf } from "@/lib/stats/distributions";
import { isMissingToken } from "@/lib/tool-kit/research-design/matrix";

/** Ratings of 3 (quite relevant) and 4 (highly relevant) count as relevant. */
export const RELEVANT_FROM = 3;

function ratingOf(cell: string): number | null {
  const value = parseNumberInput(cell);
  return value !== null && Number.isInteger(value) && value >= 1 && value <= 4
    ? value
    : null;
}

export interface CviRatings {
  /** Experts × items; null where a rating is missing or unusable. */
  values: (number | null)[][];
  missing: number;
  invalid: number;
}

export function readRatings(body: readonly (readonly string[])[]): CviRatings {
  let missing = 0;
  let invalid = 0;
  const values = body.map((row) =>
    row.map((cell) => {
      if (isMissingToken(cell)) {
        missing += 1;
        return null;
      }
      const rating = ratingOf(cell);
      if (rating === null) {
        invalid += 1;
      }
      return rating;
    })
  );
  return { values, missing, invalid };
}

/** Pc: the probability that exactly `agree` of `experts` call an item relevant by chance. */
export function chanceAgreement(experts: number, agree: number): number {
  return binomialPmf(agree, experts, 0.5);
}

/** k* = (I-CVI − Pc) / (1 − Pc). */
export function modifiedKappa(icvi: number, pc: number): number {
  return (icvi - pc) / (1 - pc);
}

export type KappaRating = "excellent" | "good" | "fair" | "poor";

/** Polit, Beck & Owen's bands, judged on k* to two decimals as their table prints it. */
export function rateKappa(kappa: number): KappaRating {
  const k = Math.round(kappa * 100) / 100;
  if (k > 0.74) {
    return "excellent";
  }
  if (k >= 0.6) {
    return "good";
  }
  if (k >= 0.4) {
    return "fair";
  }
  return "poor";
}

/** Lynn (1986), Table 2: panel size and how many experts must rate an item relevant. */
export const LYNN_TABLE: readonly { experts: number; agree: number }[] = [
  { experts: 3, agree: 3 },
  { experts: 4, agree: 4 },
  { experts: 5, agree: 5 },
  { experts: 6, agree: 5 },
  { experts: 7, agree: 6 },
  { experts: 8, agree: 7 },
  { experts: 9, agree: 7 },
  { experts: 10, agree: 8 },
];

/** The ≥ .78 floor applied to panels larger than Lynn's table. */
const LARGE_PANEL_FLOOR = 78;

export interface LynnCriterion {
  experts: number;
  /** Experts who must rate the item 3 or 4. */
  agree: number;
  /** The same as an I-CVI. */
  proportion: number;
  /** False when the panel is larger than Lynn's table and the .78 floor was applied. */
  fromTable: boolean;
}

/** Null below three experts, the minimum Lynn advised. */
export function lynnCriterion(experts: number): LynnCriterion | null {
  if (!Number.isInteger(experts) || experts < 3) {
    return null;
  }
  const row = LYNN_TABLE.find((entry) => entry.experts === experts);
  if (row) {
    return { ...row, proportion: row.agree / experts, fromTable: true };
  }
  let agree = experts;
  while (
    agree > 0 &&
    Math.round(((agree - 1) / experts) * 100) >= LARGE_PANEL_FLOOR
  ) {
    agree -= 1;
  }
  return { experts, agree, proportion: agree / experts, fromTable: false };
}

export interface CviItem {
  /** Experts who rated this item. */
  experts: number;
  /** Of those, how many rated it 3 or 4. */
  agree: number;
  icvi: number;
  pc: number;
  kappa: number;
  rating: KappaRating;
  /** Every expert who rated it called it relevant. */
  universal: boolean;
  lynn: LynnCriterion | null;
  /** Null when the panel is too small for Lynn's criterion. */
  meetsLynn: boolean | null;
}

export interface CviAnalysis {
  /** One entry per item; null for an item nobody rated. */
  items: (CviItem | null)[];
  experts: number;
  sCviAve: number;
  sCviUa: number;
  /** Each expert's share of items rated 3 or 4 — a quick check for an outlier rater. */
  expertShares: (number | null)[];
}

export function analyzeItem(ratings: readonly number[]): CviItem | null {
  const experts = ratings.length;
  if (experts === 0) {
    return null;
  }
  const agree = ratings.filter((rating) => rating >= RELEVANT_FROM).length;
  const icvi = agree / experts;
  const pc = chanceAgreement(experts, agree);
  const kappa = modifiedKappa(icvi, pc);
  const lynn = lynnCriterion(experts);
  return {
    experts,
    agree,
    icvi,
    pc,
    kappa,
    rating: rateKappa(kappa),
    universal: agree === experts,
    lynn,
    meetsLynn: lynn ? agree >= lynn.agree : null,
  };
}

function rated(values: readonly (number | null)[]): number[] {
  return values.filter((value): value is number => value !== null);
}

export function analyzeContentValidity(
  values: readonly (readonly (number | null)[])[],
  itemCount: number
): CviAnalysis | null {
  const items = Array.from({ length: itemCount }, (_, item) =>
    analyzeItem(rated(values.map((row) => row[item] ?? null)))
  );
  const scored = items.filter((item): item is CviItem => item !== null);
  if (scored.length === 0) {
    return null;
  }
  return {
    items,
    experts: values.length,
    sCviAve: mean(scored.map((item) => item.icvi)),
    sCviUa: scored.filter((item) => item.universal).length / scored.length,
    expertShares: values.map((row) => {
      const ratings = rated(row.slice(0, itemCount));
      return ratings.length === 0
        ? null
        : ratings.filter((rating) => rating >= RELEVANT_FROM).length /
            ratings.length;
    }),
  };
}
